import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Organization } from '@easydone/shared';
import { useAuth } from './AuthContext';
import { getOrganization } from '../services/authService';

interface OrgContextType {
  currentOrg: Organization | null;
  loading: boolean;
  refreshOrg: () => Promise<void>;
}

const OrgContext = createContext<OrgContextType | undefined>(undefined);

export function useOrg() {
  const context = useContext(OrgContext);
  if (!context) {
    throw new Error('useOrg must be used within OrgProvider');
  }
  return context;
}

interface OrgProviderProps {
  children: ReactNode;
}

export function OrgProvider({ children }: OrgProviderProps) {
  const { userData } = useAuth();
  const [currentOrg, setCurrentOrg] = useState<Organization | null>(null);
  const [loading, setLoading] = useState(true);

  const refreshOrg = async () => {
    if (userData?.orgId) {
      const org = await getOrganization(userData.orgId);
      setCurrentOrg(org);
    }
  };

  useEffect(() => {
    const fetchOrg = async () => {
      if (userData?.orgId) {
        const org = await getOrganization(userData.orgId);
        setCurrentOrg(org);
      } else {
        setCurrentOrg(null);
      }
      setLoading(false);
    };

    fetchOrg();
  }, [userData]);

  const value = {
    currentOrg,
    loading,
    refreshOrg,
  };

  return <OrgContext.Provider value={value}>{children}</OrgContext.Provider>;
}
